// Filters that survive leaving a page and coming back — kept in sessionStorage, so they last until the browser tab
// closes (and are wiped on sign-in / sign-out). Keys look like "page.filter" (e.g. "users.roleFilter").
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

const PREFIX = "cf:";
const listeners = new Set();
const mounted = new Map(); // key -> mount count
let version = 0;

const emit = () => { version += 1; listeners.forEach((l) => l()); };
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };

const enc = (v) => JSON.stringify(v, (_, x) => (x instanceof Set ? { __set: [...x] } : x));
const dec = (s) => JSON.parse(s, (_, x) => (x && typeof x === "object" && Array.isArray(x.__set) ? new Set(x.__set) : x));
const store = () => { try { return window.sessionStorage; } catch { return null; } };

function read(key) {
  const raw = store()?.getItem(PREFIX + key);
  if (raw == null) return undefined;
  try { return dec(raw); } catch { return undefined; }
}

function write(key, value, initial) {
  const s = store();
  if (!s) return;
  try {
    if (enc(value) === enc(initial)) s.removeItem(PREFIX + key);
    else s.setItem(PREFIX + key, enc(value));
  } catch { /* storage full or blocked — filters just won't persist */ }
  emit();
}

const blank = (v) => v === undefined || v === null || v === "";

/**
 * Drop-in for useState on a filter. `urlValue` (e.g. searchParams.get("q")) wins when present, so deep links still work.
 */
export function usePersistedState(key, initial, urlValue) {
  const initRef = useRef(undefined);
  if (initRef.current === undefined) initRef.current = typeof initial === "function" ? initial() : initial;
  const pick = useCallback((k) => {
    if (!blank(urlValue)) return urlValue;
    const saved = read(k);
    return saved === undefined ? initRef.current : saved;
  }, [urlValue]);
  const [value, setValue] = useState(() => pick(key));
  const lastKey = useRef(key);

  useEffect(() => {
    if (lastKey.current !== key) { lastKey.current = key; setValue(pick(key)); }
  }, [key, pick]);
  useEffect(() => { if (lastKey.current === key) write(key, value, initRef.current); }, [key, value]);
  useEffect(() => {
    mounted.set(key, (mounted.get(key) || 0) + 1);
    const onReset = () => setValue(initRef.current);
    window.addEventListener(`cf-reset:${key}`, onReset);
    emit();
    return () => {
      const n = (mounted.get(key) || 1) - 1;
      if (n) mounted.set(key, n); else mounted.delete(key);
      window.removeEventListener(`cf-reset:${key}`, onReset);
      emit();
    };
  }, [key]);
  return [value, setValue];
}

/** Keys of filters on screen right now that differ from their defaults. */
function dirtyMounted() {
  const s = store();
  if (!s) return [];
  return [...mounted.keys()].filter((k) => s.getItem(PREFIX + k) != null);
}

export function useDirtyFilters() {
  useSyncExternalStore(subscribe, () => version);
  return dirtyMounted();
}

/** Put every filter currently on screen back to its default. */
export function resetMountedFilters() {
  dirtyMounted().forEach((k) => {
    store()?.removeItem(PREFIX + k);
    window.dispatchEvent(new Event(`cf-reset:${k}`));
  });
  emit();
}

/** Forget every remembered filter (sign-in / sign-out). */
export function clearPersistedFilters() {
  const s = store();
  if (!s) return;
  Object.keys(s).filter((k) => k.startsWith(PREFIX)).forEach((k) => s.removeItem(k));
  emit();
}
